import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { queryKeys, invalidateQueries } from '../lib/queryKeys';
import { ApiRequestError, getApiErrorMessage } from '../lib/apiErrors';
import type { 
  TicketFilterRequest, 
  CreateTicketRequest, 
  UpdateTicketRequest,
  AddCommentRequest,
  MergeTicketRequest
} from '../types/tickets';

// Get tickets list with filters
export function useTickets(filter: TicketFilterRequest = {}, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.tickets.list(filter),
    queryFn: async () => {
      const response = await apiService.getTickets(filter);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch tickets'));
      }
      return response.data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

// Get tickets from all user's organizations (unified multi-org view)
export function useAllOrganizationTickets(filter: TicketFilterRequest = {}, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.tickets.allOrgsList(filter),
    queryFn: async () => {
      const response = await apiService.getAllOrganizationTickets(filter);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch tickets'));
      }
      return response.data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

// Get single ticket
export function useTicket(id: string) {
  return useQuery({
    queryKey: queryKeys.tickets.detail(id),
    queryFn: async () => {
      const response = await apiService.getTicket(id);
      if (!response.success || !response.data) {
        // Keeps the status so a deleted or missing ticket isn't retried - its message ("This ticket has been
        // deleted...") shows at once
        throw new ApiRequestError(getApiErrorMessage(response, 'Failed to fetch ticket'), response.statusCode);
      }
      return response.data;
    },
    enabled: !!id,
  });
}

// Get ticket stats
export function useTicketStats() {
  return useQuery({
    queryKey: queryKeys.tickets.stats(),
    queryFn: async () => {
      const response = await apiService.getTicketStats();
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch ticket stats'));
      }
      return response.data;
    },
  });
}

// Get ticket history
export function useTicketHistory(ticketId: string) {
  return useQuery({
    queryKey: queryKeys.tickets.history(ticketId),
    queryFn: async () => {
      const response = await apiService.getTicketHistory(ticketId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch ticket history'));
      }
      return response.data;
    },
    enabled: !!ticketId,
  });
}

// Create ticket mutation
export function useCreateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ticket: CreateTicketRequest) => {
      const response = await apiService.createTicket(ticket);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to create ticket'));
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate tickets list (both single-org and all-orgs views) and stats
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.lists() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.allOrgsLists() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.stats() });
      // Invalidate dashboard/reports to update stats
      queryClient.invalidateQueries({ queryKey: invalidateQueries.reports.all() });
    },
  });
}

// Update ticket mutation
export function useUpdateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateTicketRequest }) => {
      const response = await apiService.updateTicket(id, updates);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to update ticket'));
      }
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      // Only invalidate - let queries refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(id) });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.lists() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.allOrgsLists() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.stats() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.history(id) });
      // Invalidate dashboard/reports to update stats
      queryClient.invalidateQueries({ queryKey: invalidateQueries.reports.all() });
    },
  });
}

// Preview what a merge into the given target would move.
// Gated on `enabled` so nothing is fetched until a target is actually picked.
export function useMergePreview(
  sourceTicketId: string | undefined,
  targetTicketId: string | undefined,
  organizationId: string | undefined,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.tickets.mergePreview(sourceTicketId ?? '', targetTicketId ?? ''),
    queryFn: async () => {
      const response = await apiService.getMergePreview(sourceTicketId!, targetTicketId!, organizationId!);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to load merge preview'));
      }
      return response.data;
    },
    enabled: enabled && !!sourceTicketId && !!targetTicketId && !!organizationId,
    staleTime: 10000,
  });
}

// Merge ticket mutation
export function useMergeTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sourceTicketId,
      targetTicketId,
      organizationId,
      copyCustomersToTarget = true,
    }: { sourceTicketId: string; targetTicketId: string; organizationId: string; copyCustomersToTarget?: boolean }) => {
      const request: MergeTicketRequest = { targetTicketId, copyCustomersToTarget };
      const response = await apiService.mergeTicket(sourceTicketId, request, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to merge tickets'));
      }
      return response.data;
    },
    onSuccess: (_data, { sourceTicketId, targetTicketId }) => {
      // Both tickets changed: the source became a closed stub, the target gained
      // the source's conversation. Invalidate detail and history for each.
      [sourceTicketId, targetTicketId].forEach((id) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.tickets.history(id) });
      });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.lists() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.allOrgsLists() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.stats() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.reports.all() });
    },
  });
}

// Trashed tickets in an organization (admins)
export function useTrashedTickets(organizationId?: string) {
  return useQuery({
    queryKey: queryKeys.tickets.trash(organizationId),
    queryFn: async () => {
      const response = await apiService.getTrashedTickets(organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to load deleted tickets'));
      }
      return response.data ?? [];
    },
    enabled: !!organizationId,
  });
}

// Restore a ticket from the trash (admins)
export function useRestoreTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.restoreTicket(id);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to restore ticket'));
      }
      return response.data;
    },
    onSuccess: () => {
      // The ticket is back in lists, stats and its own detail view
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.all() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.reports.all() });
    },
  });
}

// Permanently delete a ticket that's already in the trash (admins)
export function usePurgeTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.purgeTicket(id);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to delete ticket permanently'));
      }
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: queryKeys.tickets.detail(id) });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.tickets.all, 'trash'] });
    },
  });
}

// Permanently redact text from a comment or, without a commentId, the ticket's description (admins)
export function useRedactTicketText() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, commentId, text }: { ticketId: string; commentId?: string; text: string }) => {
      const response = commentId
        ? await apiService.redactComment(ticketId, commentId, text)
        : await apiService.redactTicketDescription(ticketId, text);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to redact text'));
      }
      return response.data;
    },
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(ticketId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.history(ticketId) });
    },
  });
}

// Permanently delete an attachment (admins in its organization)
export function useDeleteAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ attachmentId }: { attachmentId: string; ticketId: string }) => {
      const response = await apiService.deleteAttachment(attachmentId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to delete attachment'));
      }
    },
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(ticketId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.history(ticketId) });
    },
  });
}

// Move a ticket to the trash (admins) - restorable until it's purged
export function useDeleteTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.deleteTicket(id);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to delete ticket'));
      }
      return response.data;
    },
    onSuccess: (_, id) => {
      // Remove the ticket from cache
      queryClient.removeQueries({ queryKey: queryKeys.tickets.detail(id) });
      // Invalidate tickets list (both single-org and all-orgs views) and stats
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.lists() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.allOrgsLists() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.stats() });
      // Invalidate dashboard/reports to update stats
      queryClient.invalidateQueries({ queryKey: invalidateQueries.reports.all() });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.tickets.all, 'trash'] });
    },
  });
}

// Add comment mutation
export function useAddComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, comment }: { ticketId: string; comment: AddCommentRequest }) => {
      const response = await apiService.addComment(ticketId, comment);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to add comment'));
      }
      return response.data;
    },
    onSuccess: (_, { ticketId }) => {
      // Invalidate the ticket details to refetch with new comment
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(ticketId) });
      // Invalidate ticket history to show new comment in history
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.history(ticketId) });
    },
  });
}